import { ArcadeGrid } from "@arcadeai/design-system/components/ui/atoms/arcade-grid";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { GatewayDiagram } from "@arcadeai/design-system/components/ui/templates/gateway-diagram";
import { ArrowUpRight, Plus } from "lucide-react";
import { CanProject } from "@/app/capabilities/components/can-project";
import { PROJECT } from "@/app/capabilities/permissions";

const GATEWAYS_DOCS_URL = "https://docs.arcade.dev/en/guides/mcp-gateways";

type GatewaysEmptyHeroProps = {
  onCreate: () => void;
};

export function GatewaysEmptyHero({ onCreate }: GatewaysEmptyHeroProps) {
  return (
    <section aria-labelledby="gateway-empty-state-title">
      <div className="relative min-h-[360px] w-full overflow-hidden rounded-lg border border-border/60 bg-background/35 shadow-black/5 shadow-sm md:min-h-[430px]">
        <HeroBackdrop />
        <div className="relative flex w-full flex-col items-center px-4 py-8 text-center sm:px-6 md:py-14">
          {/* Diagram gets the full hero width (capped at max-w-4xl) so
           * its @container is comfortably above the @2xl threshold and
           * the full 5/8 fan-out layout activates instead of compact. */}
          <div className="mx-auto w-full max-w-4xl">
            <GatewayDiagram />
          </div>
          <div className="mx-auto flex w-full max-w-2xl flex-col items-center">
            <h2
              className="mt-6 max-w-xl text-balance font-semibold text-foreground text-lg sm:text-xl md:mt-7"
              id="gateway-empty-state-title"
            >
              No MCP gateways yet
            </h2>
            <p className="mt-2 max-w-md text-pretty text-muted-foreground text-sm leading-relaxed">
              Create one to connect your agent to your favorite apps.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <CanProject permission={PROJECT.mcpGateways.create}>
                <Button onClick={onCreate} size="lg" type="button">
                  <Plus aria-hidden className="mr-1 size-4" />
                  Create MCP gateway
                </Button>
              </CanProject>
            </div>
            <a
              className="mt-4 inline-flex items-center gap-1 text-muted-foreground text-sm underline decoration-border underline-offset-4 transition-colors hover:text-foreground"
              href={GATEWAYS_DOCS_URL}
              rel="noopener"
              target="_blank"
            >
              Learn how gateways work
              <ArrowUpRight aria-hidden className="size-3.5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 [-webkit-mask-image:radial-gradient(ellipse_78%_70%_at_center,#000_35%,transparent_82%)] [mask-image:radial-gradient(ellipse_78%_70%_at_center,#000_35%,transparent_82%)]"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_18%,oklch(from_var(--brand-green)_l_c_h_/_0.03),transparent_60%),radial-gradient(circle_at_85%_24%,oklch(from_var(--brand-yellow)_l_c_h_/_0.04),transparent_60%),radial-gradient(circle_at_50%_96%,oklch(from_var(--brand-fuchsia)_l_c_h_/_0.02),transparent_64%)] opacity-[0.8] dark:opacity-[0.45]" />
      <ArcadeGrid className="size-full text-foreground opacity-[0.018]" />
    </div>
  );
}
